import React, {useEffect, useMemo} from "react";
import "./Message.css";
import { useTranslation } from 'react-i18next';
import ReactMarkdown from "react-markdown";
import rehypeRaw from 'rehype-raw'
import RainbowSDKService from "../services/RainbowSDKService";
import AdaptiveCardComponent from "../AdaptiveCard/AdaptiveCard";
import {getKnownContact} from "../services/UtilsService";
import rehypeExternalLinks from "rehype-external-links";
import { Message as MessageRB } from "rainbow-web-sdk";
import headsetIcon from '../img/headset.svg';
import botIcon from '../img/alieBot.svg';


type MessageContainerProps = {
    message: any;
    full_message: boolean;
    show_name: boolean;
    cluster: any;
    adaptiveCardActionsHandler: any;
};

type MessageProps = {
    cluster?: any;
    message?: MessageRB;
    adaptiveCardActionsHandler?: any;
}

const DOWNLOAD_WINDOW_CLOSE_DELAY_IN_MILLISECONDS = 1_000;

const MessageContainer: React.FC<MessageContainerProps> = ({message, full_message, show_name, cluster, adaptiveCardActionsHandler}) => {
    const [imgBlob, setImgBlob] = React.useState<Blob | null>(null);
    const [isDownloading, setIsDownloading] = React.useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (message && message.fileName && (message.fileName.includes(".png") || message.fileName.includes(".jpg"))) {
            loadImageBlob(message.fileId, message.fileName)
        }
    }, [message]);

    function saveToFile(blob: Blob, fileName: string, downloadWindow: Window | null) {
        const url = window.URL.createObjectURL(blob);
        const revokeUrl = () => window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);

        if (downloadWindow && !downloadWindow.closed) {
            downloadWindow.location.href = url;
            window.setTimeout(() => {
                if (!downloadWindow.closed) {
                    downloadWindow.close();
                }
            }, DOWNLOAD_WINDOW_CLOSE_DELAY_IN_MILLISECONDS);
            revokeUrl();
            return;
        }

        const elementA = document.createElement("a");
        elementA.style.display = "none";
        elementA.href = url;
        elementA.download = fileName || "download";
        document.body.appendChild(elementA);
        elementA.click();
        elementA.remove();
        revokeUrl();
    }

    async function fileDownload(fileId: string, fileName: string) {
        if (isDownloading) return;
        setIsDownloading(true);

        // Open the top-level browsing context while the click still has user activation.
        // Navigating it to the blob avoids the parent iframe's frame-src policy.
        const downloadWindow = window.open("about:blank", "_blank");
        if (downloadWindow) {
            downloadWindow.opener = null;
        }

        try {
            console.log("[Message] Got file with id : ", fileId);
            const fileDocument = await RainbowSDKService.getFileDocument(fileName);
            if (!fileDocument || fileDocument.length === 0) {
                downloadWindow?.close();
                console.log("[Message] Unable to get file document for id : ", fileId);
                return;
            }

            const blob = await fileDocument[0].download();
            saveToFile(blob, fileName, downloadWindow);
        }
        catch (e) {
            if (downloadWindow && !downloadWindow.closed) {
                downloadWindow.close();
            }
            console.log("[Message] Error downloading file: ", e);
        }
        finally {
            setIsDownloading(false);
        }
    }

    async function loadImageBlob(fileId: string, fileName: string) {
        try {
            let fileDocument = await RainbowSDKService.getFileDocument(fileName);
            if (!fileDocument || fileDocument.length === 0) {
                console.log("[Message] Unable to get file descriptor for file with id : ", fileId);
                return;
            }
            let blob = await fileDocument[0].download();
            setImgBlob(blob);
        }
        catch(err: any){
            console.log("[Message] Unable to download the image with id : ", fileId, " err: ", err);
        }
    }

    let {content, messageType, sideClassName, customType, icon, iconBgColor} = useMemo(() => {
        const shrink = full_message? "" : " message-shrink";
        const customType = message.customType? "-" + message.customType : "";

        let baseContent = {
            content: message.data,
            messageType:"regular",
            sideClassName: "chat-message-" + message.side + customType + shrink + " message-content",
            customType: customType,
            icon: headsetIcon,
            iconBgColor: "#6b479e"
        }

        if(message.additionalContent?.type === "text/markdown") {
            baseContent.content = message.additionalContent?.message;
        }

        if (message.additionalContent?.message?.includes("http://adaptivecards.io/schemas/adaptive-card.json")) {
            try {
                baseContent.messageType = "adaptiveCards"
                baseContent.sideClassName += "-adaptive-cards"
                baseContent.content = JSON.parse(message.additionalContent?.message)
            }catch (e) {
                console.error("[Message] Error parsing adaptive card: ", e, content)
            }
        }

        const knownContact = getKnownContact(message.from);
        console.log(message.from);
        if (knownContact) {
            baseContent.icon = botIcon;
            baseContent.iconBgColor = knownContact.iconColor;
        }
        return baseContent;
    }, [message, full_message])

    let date = useMemo(() => {
        if (message.date) {
            return new Date(message.date).toLocaleTimeString(navigator.language, {hour: '2-digit', minute:'2-digit'});
        }
    }, [message.date])


    return (
        <div key={"message" + message.id} className={cluster?"chat-message-cluster":"chat-message-container"}>
            {show_name && message.side === 'L' && message.from && <p className="firstname">{message.from.firstname}</p>}
            <div className="message">
                {
                    (full_message && message.side === 'L' && ["", "-outOfBusinessHours", "-unavailability"].includes(customType)) &&
                    <div className="icon-agent-container" style={{backgroundColor: iconBgColor}}>
                        <img className="icon-agent" src={icon} alt={(t("alt_code_icon_agent") as string)}/>
                    </div>
                }
                <div className={sideClassName}>
                    { (() => {
                        if (messageType === "adaptiveCards") {
                            return (
                                <AdaptiveCardComponent card={content} actionHandler={adaptiveCardActionsHandler}/>
                            )
                        }else if (imgBlob) {
                            return <img src={imgBlob ? URL.createObjectURL(imgBlob): ""} alt={content} className="message-image"/>;
                        }
                        else if (message.fileId) {
                            return (
                                <button
                                    type="button"
                                    className="link-button"
                                    onClick={() => fileDownload(message.fileId, message?.fileName)}
                                    disabled={isDownloading}
                                    aria-busy={isDownloading}
                                >
                                    <ReactMarkdown rehypePlugins={[[rehypeRaw], [rehypeExternalLinks, {target: '_blank'}]]}>{message?.fileName}</ReactMarkdown>
                                </button>
                            )
                        }else if (customType !== "") {
                            return content
                        }
                        else {
                            return content.split('\n').map((line: string | null | undefined, i: React.Key | null | undefined)=>
                                <ReactMarkdown rehypePlugins={[[rehypeRaw], [rehypeExternalLinks, {target: '_blank'}]]} key={i}>{line}</ReactMarkdown>
                            )
                        }
                    })()}
                    {
                        full_message && (date !== undefined) && <div className="message-timestamp">{date}</div>
                    }
                </div>
            </div>
        </div>
    )
}

const Message: React.FC<MessageProps> = ({cluster, message, adaptiveCardActionsHandler}) => {
    if(message){
        return <MessageContainer message={message} full_message={true} show_name={true} cluster={false} adaptiveCardActionsHandler={adaptiveCardActionsHandler}/>
    }else if (cluster) {
        const clusterLength= cluster.length
        return cluster.map((msg: any, i: number)=>{
            return <MessageContainer key={i} message={msg} full_message={i===clusterLength-1} show_name={i===0} cluster={true} adaptiveCardActionsHandler={adaptiveCardActionsHandler}/>
        })
    }
}

export default Message;